/* eslint-disable ember/no-controller-access-in-routes, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';
import {
  loadTemplateWithSections,
  periodLabelAt,
  templateDisplayName,
} from 'linced-app/utils/reporting';

export default class ViewRoute extends Route {
  templateName = 'reporting/group/view';

  // Which reporting period is being viewed. Historic cards route here for a
  // past period, so it cannot be assumed to be the current one. Omitted (e.g.
  // an old bookmark) falls back to the current period.
  queryParams = {
    periodIndex: { refreshModel: true },
    // Addresses the period across template versions — see the group
    // controller's qpPeriodEnd. Preferred over periodIndex when present.
    periodEndDate: { refreshModel: true },
    // The academic year the period belongs to — see the group controller's
    // qpPeriodYear. Names the screen, and narrows the report query on the
    // periodIndex fallback path, where an index alone spans every year the
    // template has run. Absent means the current year.
    periodYear: { refreshModel: true },
  };

  // Services
  @service
  dates;

  @service
  session;

  @service
  store;

  @service
  router;

  // Resolves the period when the query param is absent — an old bookmark, or a
  // direct URL. The backend owns which period a template is currently in, so
  // this asks the counts response rather than recomputing it here.
  async _currentPeriodIndex(template) {
    let groupCtrl = this.controllerFor('reporting.group');
    try {
      await groupCtrl.loadGroupReports();
    } catch {
      // Counts unavailable — fall through to the first period rather than
      // failing the transition.
    }
    let entry = (groupCtrl.templateDraftInfo || {})[template.id];
    return entry && typeof entry.periodIndex === 'number'
      ? entry.periodIndex
      : 0;
  }

  async model(params) {
    let template;
    try {
      template = await loadTemplateWithSections(this.store, params.template_id);
    } catch (err) {
      this.router.transitionTo('reporting.group');
      return;
    }

    let group = this.modelFor('reporting.group');

    // Query params arrive as strings; an absent one means "current period".
    let periodIndex =
      params.periodIndex === undefined ||
      params.periodIndex === null ||
      params.periodIndex === ''
        ? await this._currentPeriodIndex(template)
        : Number(params.periodIndex);

    // The academic year the card is showing. Not pinned to the current one:
    // viewing a past year's published reports is the main reason to arrive
    // here from a historic card.
    let year =
      params.periodYear === undefined ||
      params.periodYear === null ||
      params.periodYear === ''
        ? this.dates.currentAcademicYear
        : Number(params.periodYear);

    // consolidateTemplateVersions widens the query to every version in this
    // template's chain, and includeSections sideloads all sections (with full
    // narrative + published snapshot) in the one call, so we don't fan out a
    // section query per report.
    // filterByPeriodIndex is required: periodIndex alone is ignored by the
    // datastore, since a zero value is indistinguishable from "unset". Without
    // the period filter a learner appears once per period in `students` while
    // reportMap keeps only one of their reports, so every row resolves to the
    // same one.
    // periodEndDate reaches one period across every version of the template;
    // an index cannot, because a version that inserts or removes a period
    // renumbers the rest. Falls back to the index (against this version alone)
    // only when no date is available — an orphaned index whose period the
    // template no longer declares.
    let periodScope = {};
    if (params.periodEndDate) {
      // A resolved end date is a full calendar date, so it already pins one
      // academic year. Adding a year filter on top could only contradict it.
      periodScope.periodEndDate = params.periodEndDate;
    } else {
      periodScope.periodIndex = periodIndex;
      periodScope.filterByPeriodIndex = true;
      periodScope.consolidateTemplateVersions = !template.get('next');
      // An index does NOT pin a year — the same index exists in every academic
      // year the template has run. Without this, one student's reports from
      // several years all come back and reportMap keeps whichever arrived
      // last, so the screen can show a different year than its heading.
      periodScope.year = year;
    }
    let publishedReports = await this.store.query('reports/student-report', {
      reportTemplateId: template.get('id'),
      schoolId: this.session.activeSchool,
      groupId: group ? group.get('id') : null,
      state: StudentReportState.PUBLISHED,
      ...periodScope,
      includeSections: true,
    });

    // A published report keeps the reportTemplateId of the version it was
    // authored against (the backend never rewrites it on read), and its
    // sections reference that version's section ids. ReportView keys section
    // data by template-section id and draws from the template it is handed, so
    // each report must be rendered against its OWN version. Load every
    // referenced version with its sections and key them by report.
    let templatesById = { [template.get('id')]: template };
    let referencedTemplateIds = new Set(
      publishedReports
        .map((r) => r.belongsTo('reportTemplateId').id())
        .filter(Boolean),
    );
    await Promise.all(
      Array.from(referencedTemplateIds).map(async (templateId) => {
        if (templatesById[templateId]) return;
        // A template sideloaded by the student-report query arrives without
        // its sections; loadTemplateWithSections fetches them in that case and
        // is a no-op when they are already held. Resolving to null on failure
        // is handled below.
        let versionTemplate = await loadTemplateWithSections(
          this.store,
          templateId,
        ).catch(() => null);
        if (versionTemplate) {
          templatesById[templateId] = versionTemplate;
        }
      }),
    );

    let reportMap = {};
    let templateMap = {};
    publishedReports.forEach((r) => {
      let studentId = r.belongsTo('studentId').id();
      let reportTemplate = templatesById[r.belongsTo('reportTemplateId').id()];
      // A report whose authored template version couldn't be reloaded with its
      // sections is unavailable: its section ids won't match the route
      // template, so rendering against that fallback would show an empty or
      // mismatched snapshot. Skip it rather than render the wrong template.
      if (!reportTemplate) return;
      reportMap[studentId] = r;
      templateMap[studentId] = reportTemplate;
    });

    // No live subject/progression preload here: published reports render from
    // their frozen snapshot, and ReportView self-loads live data (per section,
    // skipping any section that has a snapshot) for the rare pre-snapshot
    // report, so an eager preload would be redundant.
    // Built from reportMap's keys rather than by mapping over the reports, so a
    // learner appears once even if they somehow hold more than one published
    // report for this period. The query is now scoped to a single period AND a
    // single year on both paths, so this is a backstop rather than the load-
    // bearing dedupe it once was.
    let students = sortBy(
      Object.keys(reportMap)
        .map((studentId) => this.store.peekRecord('person', studentId))
        .filter(Boolean),
      'fullName',
    );

    return {
      template,
      students,
      reportMap,
      templateMap,
      periodIndex,
      year,
    };
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    let groupCtrl = this.controllerFor('reporting.group');
    groupCtrl.set('isViewRoute', true);

    let template = model.template;
    // Always rebuild: displayName is set on the shared template record by
    // whichever screen touched it last, so a cached value can name a different
    // period than the one being viewed.
    let year = model.year;
    // Named for the period actually being viewed, not whichever is current —
    // they differ whenever a historic card routed here. The counts entry is
    // only authoritative when it describes this same period, which
    // templateDisplayName checks.
    let entry = (groupCtrl.templateDraftInfo || {})[template.get('id')];
    let label = periodLabelAt(template, model.periodIndex, entry);
    let displayName = templateDisplayName(
      template,
      model.periodIndex,
      year,
      entry,
    );
    template.set('displayName', displayName);
    groupCtrl.set('activeReport', template);

    // Header line for this screen only. Kept off the template record: that
    // record's displayName is shared with the review screen, the student
    // timeline and the PDF context name, none of which are group-scoped.
    let group = this.modelFor('reporting.group');
    controller.set(
      'viewHeading',
      [group && group.get('bannerText'), template.get('name'), year, label]
        .filter(Boolean)
        .join(' - '),
    );

    controller.set('viewStudents', model.students);
    controller.set('viewStudentReports', model.reportMap);
    controller.set('viewStudentTemplates', model.templateMap);

    if (model.students.length) {
      controller.selectViewStudent(model.students[0]);
    }
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      let groupCtrl = this.controllerFor('reporting.group');
      groupCtrl.set('isViewRoute', false);
      controller.cleanupViewView();
      // Period params are sticky per template. Cleared so the next visit that
      // doesn't name a period gets the current one, not whichever period was
      // last opened here (often a historic card's).
      controller.periodIndex = null;
      controller.periodEndDate = null;
      controller.periodYear = null;
    }
  }
}
